/* eslint-disable import/no-unused-modules */
import { ApplicationState, JobDetailsModel, StatusEnum } from "@amzn/hvh-simple-hire-checklist-internal-api-common";

/**
 * Defines the types of banners that can be displayed in the hiring flow.
 * Each banner serves a specific purpose in the application process.
 *
 * Update src/components/india-hiring-banners/index.tsx#IndiaHiringBanner
 * if a new banner type.
 */
export enum BannerType {
  /** Displays screening results progress */
  SCREENING = "SCREENING",

  /** Shown when start date has been denied */
  START_DATE_DENIED = "START_DATE_DENIED",

  /** Displays task reminder with associated date */
  TASK_REMINDER_WITH_DATE = "TASK_REMINDER_WITH_DATE",

  /** Shows details about the confirmed start date */
  START_DATE_DETAILS = "START_DATE_DETAILS",
}

/**
 * Properties required for banner selection logic
 * @interface BannerSelectorProps
 * @property {string} [applicationState] - Current state of the application
 * @property {string} [phoStatus] - PHO status
 * @property {JobDetailsModel} [jcDetails] - Job details information
 * @property {string} [startDate] - Candidate's start date
 * @property {string} [startDateStatus] - Status of the start date (e.g., TENTATIVE, CONFIRMED)
 */
export interface BannerSelectorProps {
  applicationState?: string;
  phoStatus?: string;
  jcDetails?: JobDetailsModel;
  startDate?: string;
  startDateStatus?: string;
}

/**
 * Handles the logic for selecting appropriate banners based on application state
 * and other contextual information.
 */
export class IndiaBannerSelector {
  /**
   * Determines which banner type to display based on application state and context.
   * @param props - Object containing application state and contextual information
   * @returns The appropriate BannerType to display
   *
   * Selection Priority:
   * 1. SCREENING banner if application is in CONTINGENCIES_PENDING state
   *
   * SCREENING Banner:
   * +---------------------------------------------------------+
   * |         We are processing your screening results        |
   * |                                                         |
   * |              0/3 Screening Result Available             |
   * |                                                         |
   * |                    [View Progress]                      |
   * +---------------------------------------------------------+
   *
   *
   *
   * 2. START_DATE_DENIED banner if:
   *    - Application is in START_DATE_CONFIRMATION_PENDING, READY_TO_HIRE, or HIRE_COMPLETE state
   *    - Start date status is DENIED
   *
   * START_DATE_DENIED Banner:
   * +----------------------------------------------------------+
   * |         The start date for this job has been denied      |
   * |                                                          |
   * |    Contact your employer to learn how to continue with   |
   * |                    your application.                     |
   * +----------------------------------------------------------+
   *
   *
   *
   * 3. START_DATE_DETAILS banner if:
   *    - Application is in START_DATE_CONFIRMATION_PENDING, READY_TO_HIRE, or HIRE_COMPLETE state
   *    - Start date is available
   *
   * START_DATE_DETAILS Banner:
   * +---------------------------------------------------------+
   * |        Thanks for attending your first day and          |
   * |                      training!                          |
   * |                                                         |
   * |              Jan 4, 2025 at 11:00 AM                    |
   * |                                                         |
   * |            Avenue 112, Baltimore, MD 21224              |
   * |                                                         |
   * |    [Add to calendar]            [View details]          |
   * +---------------------------------------------------------+
   *
   *
   *
   * 4. Defaults to TASK_REMINDER_WITH_DATE banner
   *
   * TASK_REMINDER_WITH_DATE Banner:
   *  +---------------------------------------------------------+
   * |     Complete the required tasks to continue your         |
   * |                    application                           |
   * |                                                          |
   * |         Your tentative start date is 2025-01-04          |
   * +----------------------------------------------------------+
   *
   **/
  static determineBannerType({ applicationState, startDate, startDateStatus }: BannerSelectorProps): BannerType {
    /* Logic 1: Background check status during CP (Contingencies Pending) phase */
    if (applicationState === ApplicationState.CONTINGENCIES_PENDING) {
      return BannerType.SCREENING;
    }

    /**
     * Logic 2: Start date related status for specific application states
     */
    if (
      [
        ApplicationState.START_DATE_CONFIRMATION_PENDING,
        ApplicationState.READY_TO_HIRE,
        ApplicationState.HIRE_COMPLETE,
      ].includes(applicationState as ApplicationState)
    ) {
      /**
       * TODO: Verify if "DENIED" is a valid startDateStatus
       * Current known values are "TENTATIVE" and "CONFIRMED"
       * syncing with tony and skipping this for now
       */

      // if (startDateStatus === "DENIED") {
      //   return BannerType.START_DATE_DENIED;
      // }
      if (startDate) {
        return BannerType.START_DATE_DETAILS;
      }
    }

    /**
     * Logic 3: Default case - TASK_REMINDER_WITH_DATE reminder like below
     *
     * +----------------------------------------------------------+
     * | Complete the required tasks to continue your application |
     * | Your tentative start date is Oct 1, 2025                 |
     * +----------------------------------------------------------+
     **/

    return BannerType.TASK_REMINDER_WITH_DATE;
  }
}
